"use client";

import { useState } from "react";
import { Search, Download, Filter } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import {
  JOURNAL_MAX_ENTRIES,
  useJournalStore,
} from "@/vybe-trader-client/messaging/stores/useJournalStore";
import { JournalVirtualizedLog } from "@/components/journal/JournalVirtualizedLog";
import { EmptyBlock } from "@/components/common/EmptyBlock";
import {
  useJournalFilteredEntries,
  useJournalMessageTypes,
} from "@/components/journal/useJournalFilteredEntries";
import {
  buildJournalExportFilename,
  downloadJournalCsv,
} from "@/components/journal/journalExport";

export default function JournalDesktopContent() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const entries = useJournalStore((s) => s.entries);
  const filteredEntries = useJournalFilteredEntries(
    entries,
    searchQuery,
    selectedTypes
  );
  const actionTypes = useJournalMessageTypes(entries);

  const toggleSelectedType = (type: string) => {
    setSelectedTypes((current) =>
      current.includes(type)
        ? current.filter((selectedType) => selectedType !== type)
        : [...current, type]
    );
  };

  const clearSelectedTypes = () => {
    setSelectedTypes([]);
  };

  const handleExportCsv = () => {
    downloadJournalCsv(buildJournalExportFilename(), filteredEntries);
  };

  const emptyMessage =
    searchQuery || selectedTypes.length > 0
      ? "No entries match your filters"
      : "No journal entries yet";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-2">
        <span className="shrink-0 text-xs font-semibold">Journal</span>
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-6 pl-7 text-[11px]"
          />
        </div>
        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {filteredEntries.length} / {JOURNAL_MAX_ENTRIES}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExportCsv}
          disabled={filteredEntries.length === 0}
          className="h-6 shrink-0 px-2 text-xs"
        >
          <Download className="mr-1 h-3 w-3" />
          CSV
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-6 shrink-0 px-2 text-xs"
            >
              <Filter className="mr-1 h-3 w-3" />
              {selectedTypes.length === 0
                ? "All"
                : `${selectedTypes.length}`}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-64 w-56 overflow-y-auto">
            <DropdownMenuLabel>Message types</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={clearSelectedTypes}>
              Show all
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {actionTypes.map((type) => (
              <DropdownMenuCheckboxItem
                key={type}
                checked={selectedTypes.includes(type)}
                onCheckedChange={() => toggleSelectedType(type)}
                onSelect={(event) => event.preventDefault()}
                className="text-xs"
              >
                {type}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {filteredEntries.length === 0 ? (
        <EmptyBlock
          config={{
            glyph: "▤",
            message: emptyMessage,
            action:
              searchQuery || selectedTypes.length > 0
                ? {
                    label: "clear filters",
                    onClick: () => {
                      setSearchQuery("");
                      clearSelectedTypes();
                    },
                  }
                : undefined,
          }}
        />
      ) : (
        <JournalVirtualizedLog
          entries={filteredEntries}
          emptyMessage={emptyMessage}
        />
      )}
    </div>
  );
}
